import React, { useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useRemoteRegistry } from "../registry/RemoteRegistryContext";
import { useAuth } from "../auth/AuthContext";
import { useNotifications } from "../context/NotificationContext";
import { telemetryFor, trackShellEvent } from "../telemetry";
import type { MfeBootstrap, RemoteConfig, ShellNotification } from "../types";

declare const __webpack_init_sharing__: (scope: string) => Promise<void>;
declare const __webpack_share_scopes__: { default: unknown };

/** Joins a root URL with a route's splat remainder, e.g. ("https://x.com", "metric-detail/bugSla2") -> "https://x.com/metric-detail/bugSla2". */
function buildIframeSrc(rootUrl: string, splat: string, search: string): string {
  const target = new URL(rootUrl);
  if (splat) {
    target.pathname = `${target.pathname.replace(/\/$/, "")}/${splat}`;
  }
  target.search = search;
  return target.toString();
}

async function loadRemoteModule(url: string, scope: string, module: string): Promise<MfeBootstrap> {
  await new Promise<void>((resolve, reject) => {
    if (document.querySelector(`script[src="${url}"]`)) return resolve();
    const script = document.createElement("script");
    script.src = url;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load remote entry: ${url}`));
    document.head.appendChild(script);
  });

  await __webpack_init_sharing__("default");

  const container = (window as any)[scope];
  if (!container) throw new Error(`Remote scope "${scope}" not found. Check that the remote's "name" matches the scope in remotes.config.json.`);

  await container.init(__webpack_share_scopes__.default);

  const factory = await container.get(module);
  return factory() as MfeBootstrap;
}

function IframeRemote({ remote }: { remote: RemoteConfig }) {
  const { user } = useAuth();
  const { isAdmin } = useRemoteRegistry();
  const { pushNotification } = useNotifications();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const targetOrigin = new URL(remote.url).origin;
  const { "*": splat = "" } = useParams();
  const { search } = useLocation();
  const src = buildIframeSrc(remote.url, splat, search);

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.origin !== targetOrigin) return;

      if (event.data?.type === "SHELL_READY") {
        iframeRef.current?.contentWindow?.postMessage(
          { type: "SHELL_AUTH", user, isAdmin },
          targetOrigin
        );
      }

      if (event.data?.type === "SHELL_NOTIFICATION") {
        const n = event.data.notification as Pick<ShellNotification, "type" | "title" | "message" | "url">;
        if (n?.title) pushNotification({ ...n, source: remote.label });
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [user, isAdmin, targetOrigin, pushNotification, remote.label]);

  return (
    <iframe
      ref={iframeRef}
      src={src}
      style={{ flex: 1, border: "none", width: "100%", height: "100%" }}
      title="Remote app"
    />
  );
}

function MfeRemote({ remote }: { remote: RemoteConfig }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();
  const { userPermissions } = useRemoteRegistry();
  const { pushNotification } = useNotifications();

  useEffect(() => {
    if (!remote.scope || !remote.module || !containerRef.current) return;

    let isMounted = true;
    let cleanup: (() => void) | void;

    const remoteUrl = (process.env.NODE_ENV === 'development' && remote.devUrl) ? remote.devUrl : remote.url;
    const apiBaseUrl = (process.env.NODE_ENV === 'development' && remote.devApiUrl) ? remote.devApiUrl : (remote.apiUrl ?? '');
    loadRemoteModule(remoteUrl, remote.scope, remote.module)
      .then((mod) => {
        if (!isMounted || !containerRef.current) return;
        const push = (n: Pick<ShellNotification, "type" | "title" | "message" | "url">) =>
          pushNotification({ ...n, source: remote.label });
        cleanup = mod.mount(containerRef.current, {
          basePath: remote.route,
          auth,
          permissions: userPermissions,
          pushNotification: push,
          apiBaseUrl,
          telemetry: telemetryFor(remote.name),
        });
      })
      .catch((err: Error) => {
        if (isMounted) setError(err.message);
      });

    return () => {
      isMounted = false;
      if (typeof cleanup === "function") cleanup();
    };
  }, [remote]);

  if (error) {
    return (
      <div style={{ padding: "2rem", color: "#c62828" }}>
        <strong>Failed to load app</strong>
        <pre style={{ marginTop: "0.5rem", fontSize: "0.85rem" }}>{error}</pre>
      </div>
    );
  }

  return <div ref={containerRef} style={{ flex: 1 }} />;
}

export function MfeLoader() {
  const { appName } = useParams<{ appName: string }>();
  const { allRemotes } = useRemoteRegistry();
  const remote = allRemotes.find((r) => r.name === appName);

  // Fired by the loader so per-remote usage counts exist even for remotes
  // that never adopt the telemetry prop (covers iframe remotes too).
  useEffect(() => {
    if (remote) trackShellEvent("remote.mounted", { remote: remote.name });
  }, [remote?.name]);

  if (!remote) {
    return (
      <div style={{ padding: "2rem" }}>
        App &ldquo;{appName}&rdquo; is not registered.
      </div>
    );
  }

  if (remote.type === "iframe") {
    return <IframeRemote remote={remote} />;
  }

  return <MfeRemote remote={remote} />;
}


